import type { AssistantQuestionPrompt, ClarifyingQuestion } from '@lixionary-jira/domain';
import { Send } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../ui/Button';
import { Input, Textarea } from '../../ui/forms';

/** The requester's answer to one question: clicked options, plus their own text when they chose "Other". */
export interface QuestionAnswer {
  picked: string[];
  /** null while "Other" is not chosen. */
  other: string | null;
}

const empty = (): QuestionAnswer => ({ picked: [], other: null });
const answered = (a: QuestionAnswer) => a.picked.length > 0 || !!a.other?.trim();

/** The answers as one chat message, in the order the questions were asked. */
export function formatAnswers(prompt: AssistantQuestionPrompt, answers: QuestionAnswer[], note = ''): string {
  const lines = prompt.questions.map((q, i) => {
    const a = answers[i] ?? empty();
    const own = a.other?.trim();
    const parts = [...a.picked, ...(own ? [q.options.length ? `Other: ${own}` : own] : [])];
    return `${i + 1}. ${q.question}\n   → ${parts.length ? parts.join('; ') : '(no answer)'}`;
  });
  const extra = note.trim();
  return `${lines.join('\n')}${extra ? `\n\nAnything else: ${extra}` : ''}`;
}

/**
 * Clarifying questions as clickable choices. Every question also takes the requester's own answer ("Other"), and
 * all answers are sent back as one message.
 */
export function QuestionForm({
  prompt,
  sending,
  disabled,
  onSend,
  onFreeText,
}: {
  prompt: AssistantQuestionPrompt;
  sending: boolean;
  disabled: boolean;
  onSend: (content: string) => void;
  onFreeText: () => void;
}) {
  const [answers, setAnswers] = useState<QuestionAnswer[]>(() => prompt.questions.map(empty));
  const [note, setNote] = useState('');
  const update = (i: number, next: QuestionAnswer) =>
    setAnswers((all) => all.map((a, j) => (j === i ? next : a)));
  const ready = answers.some(answered);

  return (
    <form
      className="question-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !disabled) onSend(formatAnswers(prompt, answers, note));
      }}
    >
      {prompt.questions.map((q, i) => (
        <QuestionField
          key={i}
          index={i}
          question={q}
          answer={answers[i] ?? empty()}
          disabled={disabled}
          onChange={(a) => update(i, a)}
        />
      ))}
      <Textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={2000}
        placeholder="Anything else the assistant should know? (optional)"
        aria-label="Anything else"
        disabled={disabled}
      />
      <div className="row between">
        <Button variant="ghost" size="sm" onClick={onFreeText} disabled={disabled}>
          Reply in your own words instead
        </Button>
        <Button
          type="submit"
          size="sm"
          icon={<Send size={14} />}
          loading={sending}
          disabled={!ready || disabled}
        >
          Send answers
        </Button>
      </div>
    </form>
  );
}

function QuestionField({
  index,
  question,
  answer,
  disabled,
  onChange,
}: {
  index: number;
  question: ClarifyingQuestion;
  answer: QuestionAnswer;
  disabled: boolean;
  onChange: (a: QuestionAnswer) => void;
}) {
  const label = `${index + 1}. ${question.question}`;
  if (!question.options.length)
    return (
      <fieldset className="question-field">
        <legend>{label}</legend>
        <Input
          value={answer.other ?? ''}
          onChange={(e) => onChange({ picked: [], other: e.target.value })}
          maxLength={1000}
          placeholder="Your answer"
          aria-label={question.question}
          disabled={disabled}
        />
      </fieldset>
    );

  const multi = question.multiSelect;
  const toggle = (option: string) => {
    const on = answer.picked.includes(option);
    if (multi)
      onChange({
        ...answer,
        picked: on ? answer.picked.filter((p) => p !== option) : [...answer.picked, option],
      });
    else onChange({ picked: on ? [] : [option], other: null });
  };
  const toggleOther = () => {
    if (answer.other !== null) onChange({ ...answer, other: null });
    else onChange({ picked: multi ? answer.picked : [], other: '' });
  };

  return (
    <fieldset className="question-field">
      <legend>
        {label}
        {multi && <span className="muted small"> · choose any</span>}
      </legend>
      <div className="question-options" role="group" aria-label={question.question}>
        {question.options.map((o) => (
          <button
            key={o}
            type="button"
            className="chip chip-button question-option"
            aria-pressed={answer.picked.includes(o)}
            onClick={() => toggle(o)}
            disabled={disabled}
          >
            {o}
          </button>
        ))}
        <button
          type="button"
          className="chip chip-button question-option"
          aria-pressed={answer.other !== null}
          onClick={toggleOther}
          disabled={disabled}
        >
          Other…
        </button>
      </div>
      {answer.other !== null && (
        <Input
          value={answer.other}
          onChange={(e) => onChange({ ...answer, other: e.target.value })}
          maxLength={1000}
          placeholder="Your own answer"
          aria-label={`Your own answer to: ${question.question}`}
          disabled={disabled}
          autoFocus
        />
      )}
    </fieldset>
  );
}

/** Questions from an earlier round, read-only; the requester's answers follow in the next message. */
export function QuestionList({ prompt }: { prompt: AssistantQuestionPrompt }) {
  return (
    <ol className="question-list">
      {prompt.questions.map((q, i) => (
        <li key={i}>
          {q.question}
          {q.options.length > 0 && (
            <div className="question-options">
              {q.options.map((o) => (
                <span key={o} className="chip">
                  {o}
                </span>
              ))}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
